import { SharedEvents, type AppointmentIdCopiedPayload } from '@satellite/analytics/sharedEvents';
import type { YardTaskChecklist } from '@nova/core';

export type { AppointmentIdCopiedPayload };

/** Human-readable event names sent to Mixpanel. Event strings are kept identical to the retired
 * legacy MixpanelEvents.js entries to preserve Mixpanel history/continuity. */
export const Events = {
  APPOINTMENT: {
    STATUS_UPDATED: 'Updated: Appointment Status',
    SEARCHED: 'Action: Search Appointments',
    // Fired from the shared appointmentMixinBase (libs/satellite), also consumed by Luna.
    ID_COPIED: SharedEvents.APPOINTMENT_ID_COPIED
  },
  SYSTEM: {
    // Fired from the shared monitorWebsocketConnectionMixin (libs/satellite).
    WEBSOCKET_CONNECTION_ERROR: SharedEvents.WEBSOCKET_CONNECTION_ERROR
  },
  SELF_CHECK_IN: {
    APPOINTMENT_LINKED: 'Action: Asset and Appointment Linked',
    APPOINTMENT_UNLINKED: 'Action: Asset and Appointment Unlinked',
    CTA_CLICKED: 'Clicked: Gate Management CTA',
    REQUESTED: 'Action: Self Check-in Requested',
    GATE_MANAGEMENT_REQUESTED: 'Action: Gate Management Requested'
  },
  GATE_FEED: {
    VIEW_PAGE: 'Viewed: Page',
    CONTACT_SALES: 'Clicked: Contact Sales'
  },
  CARRIER: {
    CREATED: 'Created: Carrier Contact'
  },
  COMPANY: {
    CREATED: 'Created: Carrier Company'
  },
  YARD: {
    ASSET_VISIT_CREATED: 'Created: Asset Visit',
    ASSET_CONTAINER_CREATED: 'Created: Asset Container',
    ASSET_CONTAINER_STATUS_UPDATED: 'Updated: Asset Container Status',
    ASSET_CONTAINER_UPDATED: 'Updated: Asset Container',
    ASSET_VISIT_APPOINTMENT_LINKED: 'Linked: Asset Visit Appointment',
    ASSET_VISIT_APPOINTMENT_UNLINKED: 'Unlinked: Asset Visit Appointment',
    ASSET_CONTAINER_APPOINTMENT_LINKED: 'Linked: Asset Container Appointment',
    ASSET_CONTAINER_APPOINTMENT_UNLINKED: 'Unlinked: Asset Container Appointment',
    GO_TO_SPOT_CLICKED: 'Clicked: Go to Spot',
    ASSET_CONTAINER_DROPPED: 'Dropped: Asset Container',
    ASSET_CONTAINER_HOOKED: 'Hooked: Asset Container',
    ASSET_VISIT_DEPARTED: 'Departed: Asset Visit',
    ASSET_ASSIGNED_TO_SPOT: 'Assigned: Asset to Spot',
    ASSET_REASSIGNED_TO_SPOT: 'Reassigned: Asset to Spot',
    ASSETS_EXPORTED: 'Exported: Yard Assets'
  },
  READY_FOR_PICKUP: {
    NOTIFICATION_SENT: 'Sent: Ready for Pickup Notification',
    NOTIFICATION_SKIPPED: 'Skipped: Ready for Pickup Notification'
  },
  CUSTOM_FORMS: {
    ACTIVATE_FIELD: 'Activated: Field',
    DEACTIVATE_FIELD: 'Deactivated: Field'
  },
  REPORTING: {
    VIEW_PAGE: 'Viewed: Insights Page',
    REPORTING_EXPORTED: 'Action: Report Exported'
  },
  TASKS: {
    CREATED: 'Created: Yard Task',
    UPDATED: 'Updated: Yard Task'
  },
  NAVIGATION: {
    ADD_APPOINTMENT_CLICKED: 'Clicked: Create Appointment (Side Nav)',
    SEARCH_APPOINTMENTS_CLICKED: 'Clicked: Search Appointments (Side Nav)',
    WAREHOUSES_CLICKED: 'Clicked: Warehouses (Side Nav)',
    MY_ORGANIZATION_CLICKED: 'Clicked: My Organization (Side Nav)',
    USERS_CLICKED: 'Clicked: Users (Side Nav)',
    MY_CARRIER_CONTACTS_CLICKED: 'Clicked: My Carrier Contacts (Side Nav)',
    INSIGHTS_CLICKED: 'Clicked: Insights (Side Nav)',
    WAREHOUSE_SELECTOR_CLICKED: 'Clicked: Warehouse Selector'
  }
} as const;

/** Payload type for "Updated: Appointment Status". Built in modules/appointments/store.js from a rich appointment payload. */
export interface AppointmentStatusUpdatedPayload {
  'Org ID'?: string | null;
  'Org Name'?: string | null;
  'Warehouse ID'?: string | null;
  'Warehouse Name'?: string | null;
  'Appointment ID'?: string | null;
  'Appointment Start'?: string | null;
  'Appointment Ref #'?: string | null;
  'Appointment Tags'?: unknown;
  'Dwell Time': string;
  'Arrival Time': string;
  'Current Status': string;
  'Update Type': string;
  'Appointment ETA'?: string | null;
  'Changed By': string;
  'Entry Point': string;
}

/** Payload type for "Action: Search Appointments". */
export interface AppointmentsSearchedPayload {
  'Org Name': string;
  'Org ID': string;
  'Search Appointments Input': string;
  'Search Appointments Filters': object;
  'Entry Point': string;
}

/** Payload type for "Action: Asset and Appointment Linked". */
export interface SelfCheckInAppointmentLinkedPayload {
  'Warehouse ID'?: string | null;
  'Warehouse Name'?: string | null;
  'Org ID'?: string | null;
  'Org Name'?: string | null;
  'Appointment ID'?: string | null;
  'Asset ID'?: string | null;
  'Existing Appointment': 'Yes' | 'No';
}

/** Payload type for "Action: Asset and Appointment Unlinked". */
export interface SelfCheckInAppointmentUnlinkedPayload {
  'Warehouse ID'?: string | null;
  'Warehouse Name'?: string | null;
  'Org ID'?: string | null;
  'Org Name'?: string | null;
  'Asset ID'?: string | null;
  'Appointment ID'?: string | null;
}

/**
 * Payload type for "Clicked: Gate Management CTA". `CTA Type` and `Entry Point` are both optional
 * since different callers historically populated one or the other (never both).
 */
export interface SelfCheckInCtaClickedPayload {
  'CTA Type'?: string;
  'Entry Point'?: string;
  'Org ID'?: string | null;
  'Org Name'?: string | null;
  'Warehouse ID'?: string | null;
  'Warehouse Name'?: string | null;
}

/** Payload type for "Action: Self Check-in Requested". */
export interface SelfCheckInRequestedPayload {
  'Entry Point': string;
  'Org ID'?: string | null;
  'Org Name'?: string | null;
  'Warehouse ID'?: string | null;
  'Warehouse Name'?: string | null;
}

/** Payload type shared by the Gate Feed "Viewed: Page" and "Clicked: Contact Sales" events. */
export interface GateFeedEventPayload {
  org_id?: string | null;
  org_name?: string | null;
  warehouse_id?: string | null;
  warehouse_name?: string | null;
  entry_point: string;
}

/** Payload type for "Created: Carrier Contact". */
export interface CarrierContactCreatedPayload {
  'Entry Point': string;
  'Carrier Contact Name': string;
  'Carrier Contact Email': string;
  'Carrier Company ID'?: string;
  'Carrier Company Name'?: string;
  'Org Name': string;
  'Org ID': string;
}

/** Payload type for "Created: Carrier Company". */
export interface CarrierCompanyCreatedPayload {
  'Entry Point': string;
  'Carrier Company Name': string;
  'SCAC Code'?: string;
  'Org Name': string;
  'Org ID': string;
}

/**
 * Fields common to (almost) every Yard event, mirroring the legacy generic `trackMixpanelEvent`
 * eventData builder in useYard.js. Kept optional here since not every event populates every field.
 */
export interface YardEventBasePayload {
  org_id?: string | null;
  org_name?: string | null;
  warehouse_id?: string | null;
  warehouse_name?: string | null;
  area_id?: string | null;
  spot_id?: string | null;
  spot_code?: string | null;
  spot_type?: string | null;
  asset_visit_id?: string | null;
  asset_visit_type?: string | null;
  asset_container_id?: string | null;
  asset_container_type?: string | null;
  checkin_type?: string | null;
  entry_point?: string | null;
}

export interface YardAssetVisitCreatedPayload extends YardEventBasePayload {
  is_planned: boolean;
  asset_container_count: number;
}

export interface YardAssetContainerCreatedPayload extends YardEventBasePayload {
  asset_container_count: number;
  asset_container_code?: string | null;
}

export interface YardAssetContainerStatusUpdatedPayload extends YardEventBasePayload {
  asset_container_status_change: string;
  drop_appointment_id?: string | null;
  pickup_appointment_id?: string | null;
}

export interface YardAssetContainerUpdatedPayload extends YardEventBasePayload {
  field?: string;
}

export interface YardAssetVisitAppointmentLinkedPayload extends YardEventBasePayload {
  appointmentId: string;
  existing_appointment: boolean;
}

export type YardAssetVisitAppointmentUnlinkedPayload = YardEventBasePayload;

export interface YardAssetContainerAppointmentLinkedPayload extends YardEventBasePayload {
  appointment_id: string;
  asset_container_code?: string | null;
  existing_appointment: boolean;
  appointment_type: string;
}

export interface YardAssetContainerAppointmentUnlinkedPayload extends YardEventBasePayload {
  asset_container_code?: string | null;
  appointment_type: string;
}

export type YardGoToSpotClickedPayload = YardEventBasePayload;

export interface YardAssetContainerDroppedPayload extends YardEventBasePayload {
  asset_container_action: string;
  asset_visit_action: string;
  appointment_id?: string | null;
}

export interface YardAssetContainerHookedPayload extends YardEventBasePayload {
  appointment_id?: string | null;
}

export interface YardAssetVisitDepartedPayload extends YardEventBasePayload {
  departed_with_asset_container: boolean;
  appointment_id?: string | null;
  is_asset_container?: boolean;
}

export type YardAssetAssignedToSpotPayload = YardEventBasePayload;

export interface YardAssetReassignedToSpotPayload extends YardEventBasePayload {
  old_spot_id?: string | null;
  old_spot_code?: string | null;
}

export interface YardAssetsExportedPayload extends YardEventBasePayload {
  export_type: string;
  filter_by: string;
}

/** Payload type for "Sent: Ready for Pickup Notification". */
export interface ReadyForPickupNotificationSentPayload {
  asset_container_id?: string | null;
  asset_container_type?: string | null;
  asset_visit_id?: string | null;
  entry_point?: string | null;
  pickup_appointment_id?: string | null;
  number_of_contacts: number;
}

/** Payload type for "Skipped: Ready for Pickup Notification". */
export interface ReadyForPickupNotificationSkippedPayload {
  asset_container_id?: string | null;
  asset_container_type?: string | null;
  asset_visit_id?: string | null;
  entry_point?: string | null;
  pickup_appointment_id?: string | null;
}

/**
 * Payload type for "Activated: Field" / "Deactivated: Field". Fired from two different UIs (the
 * field edit dialog and the custom forms field selector) that don't both have every property in
 * scope — callers send `null` for whatever they can't populate so both send the same shape.
 */
export interface CustomFormsFieldActivationPayload {
  'Field Label': string;
  'Field Type': string;
  'Custom Form Application': string | null;
  'Custom Form Type': string | null;
  'Warehouse ID': string | null;
  'Warehouse Name': string | null;
  'Entry Point': string;
  'Org ID': string;
  'Org Name': string;
}

/** Payload type for "Viewed: Insights Page". */
export interface ReportingViewPagePayload {
  'Org Name': string;
  'Org ID': string;
  'Insights Tab': string;
}

/** Payload type for "Action: Report Exported". */
export interface ReportingExportedPayload {
  'Org Name': string;
  'Org ID': string;
  'Report Fields': string[];
  'Report On': string;
}

/** The two supported yard task kinds. Named `YardTaskTypeValue` to avoid clashing with the
 * `YardTaskType` enum exported by `@nova/core`. */
export type YardTaskTypeValue = 'move' | 'check';

/**
 * Payload type shared by "Created: Yard Task" and "Updated: Yard Task" — both events report the
 * same task snapshot fields. `has_attachment` and the tags/checklist arrays are always present;
 * the rest of the move-/check-specific fields are optional since only one set applies depending
 * on `yard_task_type` (mirrors Circe's `YardTaskOpenedPayload`).
 */
export interface YardTaskPayload {
  entry_point: string;
  yard_task_id: string;
  yard_task_type: YardTaskTypeValue;
  yard_task_assignee?: string;
  yard_task_duedate?: string;
  yard_task_description?: string;
  yard_task_tags?: string[];
  has_attachment: boolean;

  // Move-specific
  asset_container_id?: string;
  asset_container_code?: string;
  origin_spot_id?: string;
  origin_spot_code?: string;
  destination_spot_id?: string;
  destination_spot_code?: string;

  // Check-specific
  check_entity_name?: string;
  check_entity_id?: string;
  yard_task_category?: string;
  yard_task_checklist?: YardTaskChecklist[];
}

/** Payload type shared by all side-nav link click events. */
export interface SideNavClickedPayload {
  screen_size: string;
}

/** Payload type for "Clicked: Warehouse Selector". */
export interface WarehouseSelectorClickedPayload {
  screen_size: string;
  page: string;
}

/** Map event keys to payload types (index signature for Satellite AnalyticsEventsMap contract). */
export type HeliosEventsMap = {
  [Events.APPOINTMENT.STATUS_UPDATED]: AppointmentStatusUpdatedPayload;
  [Events.APPOINTMENT.SEARCHED]: AppointmentsSearchedPayload;
  [Events.SELF_CHECK_IN.APPOINTMENT_LINKED]: SelfCheckInAppointmentLinkedPayload;
  [Events.SELF_CHECK_IN.APPOINTMENT_UNLINKED]: SelfCheckInAppointmentUnlinkedPayload;
  [Events.SELF_CHECK_IN.CTA_CLICKED]: SelfCheckInCtaClickedPayload;
  [Events.SELF_CHECK_IN.REQUESTED]: SelfCheckInRequestedPayload;
  [Events.SELF_CHECK_IN.GATE_MANAGEMENT_REQUESTED]: SelfCheckInRequestedPayload;
  [Events.GATE_FEED.VIEW_PAGE]: GateFeedEventPayload;
  [Events.GATE_FEED.CONTACT_SALES]: GateFeedEventPayload;
  [Events.CARRIER.CREATED]: CarrierContactCreatedPayload;
  [Events.COMPANY.CREATED]: CarrierCompanyCreatedPayload;
  [Events.YARD.ASSET_VISIT_CREATED]: YardAssetVisitCreatedPayload;
  [Events.YARD.ASSET_CONTAINER_CREATED]: YardAssetContainerCreatedPayload;
  [Events.YARD.ASSET_CONTAINER_STATUS_UPDATED]: YardAssetContainerStatusUpdatedPayload;
  [Events.YARD.ASSET_CONTAINER_UPDATED]: YardAssetContainerUpdatedPayload;
  [Events.YARD.ASSET_VISIT_APPOINTMENT_LINKED]: YardAssetVisitAppointmentLinkedPayload;
  [Events.YARD.ASSET_VISIT_APPOINTMENT_UNLINKED]: YardAssetVisitAppointmentUnlinkedPayload;
  [Events.YARD.ASSET_CONTAINER_APPOINTMENT_LINKED]: YardAssetContainerAppointmentLinkedPayload;
  [Events.YARD.ASSET_CONTAINER_APPOINTMENT_UNLINKED]: YardAssetContainerAppointmentUnlinkedPayload;
  [Events.YARD.GO_TO_SPOT_CLICKED]: YardGoToSpotClickedPayload;
  [Events.YARD.ASSET_CONTAINER_DROPPED]: YardAssetContainerDroppedPayload;
  [Events.YARD.ASSET_CONTAINER_HOOKED]: YardAssetContainerHookedPayload;
  [Events.YARD.ASSET_VISIT_DEPARTED]: YardAssetVisitDepartedPayload;
  [Events.YARD.ASSET_ASSIGNED_TO_SPOT]: YardAssetAssignedToSpotPayload;
  [Events.YARD.ASSET_REASSIGNED_TO_SPOT]: YardAssetReassignedToSpotPayload;
  [Events.YARD.ASSETS_EXPORTED]: YardAssetsExportedPayload;
  [Events.READY_FOR_PICKUP.NOTIFICATION_SENT]: ReadyForPickupNotificationSentPayload;
  [Events.READY_FOR_PICKUP.NOTIFICATION_SKIPPED]: ReadyForPickupNotificationSkippedPayload;
  [Events.CUSTOM_FORMS.ACTIVATE_FIELD]: CustomFormsFieldActivationPayload;
  [Events.CUSTOM_FORMS.DEACTIVATE_FIELD]: CustomFormsFieldActivationPayload;
  [Events.REPORTING.VIEW_PAGE]: ReportingViewPagePayload;
  [Events.REPORTING.REPORTING_EXPORTED]: ReportingExportedPayload;
  [Events.APPOINTMENT.ID_COPIED]: AppointmentIdCopiedPayload;
  [Events.SYSTEM.WEBSOCKET_CONNECTION_ERROR]: void;
  [Events.TASKS.CREATED]: YardTaskPayload;
  [Events.TASKS.UPDATED]: YardTaskPayload;
  [Events.NAVIGATION.ADD_APPOINTMENT_CLICKED]: SideNavClickedPayload;
  [Events.NAVIGATION.SEARCH_APPOINTMENTS_CLICKED]: SideNavClickedPayload;
  [Events.NAVIGATION.WAREHOUSES_CLICKED]: SideNavClickedPayload;
  [Events.NAVIGATION.MY_ORGANIZATION_CLICKED]: SideNavClickedPayload;
  [Events.NAVIGATION.USERS_CLICKED]: SideNavClickedPayload;
  [Events.NAVIGATION.MY_CARRIER_CONTACTS_CLICKED]: SideNavClickedPayload;
  [Events.NAVIGATION.INSIGHTS_CLICKED]: SideNavClickedPayload;
  [Events.NAVIGATION.WAREHOUSE_SELECTOR_CLICKED]: WarehouseSelectorClickedPayload;
} & Record<string, object | void>;
